import React from "react";
import PropTypes from "prop-types";
import { MdSearch, MdRefresh, MdAdd } from "react-icons/md";

/**
 * Composant ActionBar réutilisable qui fournit une barre de recherche et des boutons d'action
 * 
 * @param {Object} props - Les propriétés du composant
 * @param {string} [props.searchTerm] - Le terme de recherche actuel
 * @param {Function} [props.onSearchChange] - Fonction appelée lorsque le terme de recherche change
 * @param {boolean} [props.showSearch=true] - Indique si la barre de recherche doit être affichée
 * @param {string} [props.searchPlaceholder="Rechercher..."] - Le texte placeholder pour la barre de recherche
 * @param {string} [props.searchIcon] - Icône personnalisée pour la recherche (par défaut: MdSearch)
 * @param {boolean} [props.showRefresh=true] - Indique si le bouton de rafraîchissement doit être affiché
 * @param {Function} [props.onRefresh] - Fonction appelée lors du clic sur le bouton de rafraîchissement
 * @param {boolean} [props.showCreateButton=true] - Indique si le bouton de création doit être affiché
 * @param {Function} [props.onCreateNew] - Fonction appelée lors du clic sur le bouton de création
 * @param {string} [props.buttonLabel="Nouveau"] - Texte du bouton de création
 * @param {string} [props.buttonIcon] - Icône personnalisée pour le bouton de création (par défaut: MdAdd)
 * @param {string} [props.primaryColor="#57A863"] - Couleur principale pour les éléments d'UI
 * @param {string} [props.hoverColor="#4c9657"] - Couleur au survol pour les éléments d'UI
 * @param {boolean} [props.loading=false] - État de chargement
 * @param {React.ReactNode} [props.customActions] - Actions personnalisées à afficher à côté des boutons standards
 * @param {string} [props.className] - Classes CSS supplémentaires pour le conteneur
 */
const ActionBar = ({
  searchTerm = "",
  onSearchChange,
  showSearch = true,
  searchPlaceholder = "Rechercher...",
  searchIcon: SearchIcon = MdSearch,
  
  showRefresh = true,
  onRefresh,
  
  showCreateButton = true,
  onCreateNew,
  buttonLabel = "Nouveau",
  buttonIcon: ButtonIcon = MdAdd,
  
  primaryColor = "#57A863",
  hoverColor = "#4c9657",
  
  loading = false,
  customActions,
  className = "",
}) => {
  // Style dynamique pour les éléments utilisant la couleur primaire
  const primaryColorStyle = {
    backgroundColor: primaryColor,
    "--hover-color": hoverColor,
  };
  
  return (
    <div className={`mb-8 flex flex-wrap items-center justify-between gap-4 ${className}`}>
      {showSearch && onSearchChange && (
        <div className="relative min-w-[200px] max-w-xs flex-1">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <SearchIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="search"
            className="w-full rounded-md border border-gray-300 px-3 py-2 pl-10 focus:outline-none"
            style={{
              "--tw-ring-color": primaryColor,
              "--tw-border-opacity": 1,
              "--tw-ring-opacity": 0.2,
              "--tw-border-color": `${primaryColor}`,
            }}
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
      )}

      <div className="flex items-center gap-3">
        {customActions}
        
        {showRefresh && onRefresh && (
          <button
            onClick={onRefresh}
            className="flex items-center justify-center rounded-md bg-white p-2 text-gray-700 transition-all hover:bg-gray-100 border border-gray-300 shadow-sm"
            title="Rafraîchir"
            disabled={loading}
          >
            <MdRefresh className={`h-5 w-5 ${loading ? "animate-spin" : ""}`} />
          </button>
        )}

        {showCreateButton && onCreateNew && (
          <button
            onClick={onCreateNew}
            className="flex items-center gap-2 rounded-md px-4 py-2 text-white transition-all shadow-sm"
            style={{
              ...primaryColorStyle,
              transition: "background-color 0.2s ease-in-out",
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = hoverColor}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = primaryColor}
            disabled={loading}
          >
            <ButtonIcon className="h-5 w-5" />
            <span>{buttonLabel}</span>
          </button>
        )}
      </div>
    </div>
  );
};

ActionBar.propTypes = {
  searchTerm: PropTypes.string,
  onSearchChange: PropTypes.func,
  showSearch: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  searchIcon: PropTypes.elementType,
  
  showRefresh: PropTypes.bool,
  onRefresh: PropTypes.func,
  
  showCreateButton: PropTypes.bool,
  onCreateNew: PropTypes.func,
  buttonLabel: PropTypes.string,
  buttonIcon: PropTypes.elementType,
  
  primaryColor: PropTypes.string,
  hoverColor: PropTypes.string,
  
  loading: PropTypes.bool,
  customActions: PropTypes.node,
  className: PropTypes.string,
};

export default ActionBar;